
import React from 'react';
import { testimonials } from '../../../mocks/equipmentHireCatalogue';
import { useScrollAnimation, useStaggeredAnimation } from '../../../hooks/useScrollAnimation';

/**
 * HireTestimonials component
 *
 * Renders a list of client testimonials with scroll‑based entrance animations.
 * Includes defensive checks and graceful fallback if the testimonials data is missing
 * or malformed.
 */
export default function HireTestimonials() {
  // Guard against undefined / null testimonials array
  const safeTestimonials = Array.isArray(testimonials) ? testimonials : [];

  // Scroll‑animation hooks
  const { ref: titleRef, isVisible: titleVisible } = useScrollAnimation();
  const {
    ref: gridRef,
    isVisible: gridVisible,
    getItemDelay,
  } = useStaggeredAnimation(safeTestimonials.length, { staggerDelay: 200 });

  return (
    <section className="py-20 bg-white">
      <div className="max-w-6xl mx-auto px-6">
        {/* Title block */}
        <div
          ref={titleRef}
          className={`text-center mb-14 transition-all duration-700 ${
            titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-amber-50 border border-amber-200 mb-4">
            <i className="ri-star-line text-amber-600 text-sm"></i>
            <span className="text-amber-700 text-xs tracking-widest uppercase font-semibold">
              Client Reviews
            </span>
          </div>
          <h2 className="font-display text-4xl md:text-5xl text-stone-900 mb-4">
            <strong>What Our Clients Say</strong>
          </h2>
          <p className="text-stone-500 text-sm max-w-xl mx-auto leading-relaxed">
            Trusted by hundreds of Melbourne event planners, couples, and corporate teams for premium equipment hire.
          </p>
        </div>

        {/* Testimonials grid */}
        <div ref={gridRef} className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {safeTestimonials.map((t, index) => (
            <div
              key={t.name ?? `testimonial-${index}`}
              style={getItemDelay(index)}
              className={`bg-stone-50 rounded-lg p-6 border border-stone-100 hover:border-amber-200 transition-all duration-500 ${
                gridVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
            >
              {/* Rating stars */}
              <div className="flex items-center gap-1 mb-4">
                {Array.from({ length: t.rating ?? 0 }).map((_, i) => (
                  <i key={i} className="ri-star-fill text-amber-400 text-sm"></i>
                ))}
              </div>

              {/* Testimonial text */}
              <p className="text-stone-600 text-sm leading-relaxed mb-5 italic">
                &ldquo;{t.text ?? ''}&rdquo;
              </p>

              {/* Author info */}
              <div className="pt-4 border-t border-stone-200">
                <p className="text-stone-900 text-sm font-semibold">{t.name ?? 'Anonymous'}</p>
                <p className="text-stone-400 text-xs">
                  {t.event ?? ''} {t.event && t.location ? '·' : ''} {t.location ?? ''}
                </p>
              </div>
            </div>
          ))}

          {/* Fallback UI when there are no testimonials */}
          {safeTestimonials.length === 0 && (
            <p className="col-span-full text-center text-stone-500">
              No client reviews available at the moment.
            </p>
          )}
        </div>
      </div>
    </section>
  );
}
